import { Instagram, Facebook, Youtube, Linkedin } from "lucide-react";
import { useLang } from "@/i18n";

const TikTokIcon = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z" />
  </svg>
);

const SOCIALS = [
  { icon: Instagram, label: "instagram", href: "https://www.instagram.com/dream2reality_football_agency" },
  { icon: Facebook, label: "facebook", href: "https://www.facebook.com/share/19LL6g8Ty8/" },
  { icon: Youtube, label: "youtube", href: "https://youtube.com/@d2rfootballagency" },
  { icon: TikTokIcon, label: "tiktok", href: "https://www.tiktok.com/@dream2realityfa" },
  { icon: Linkedin, label: "linkedin", href: "https://www.linkedin.com/company/dream2reality-football-agency/" },
];

const STADIUM_IMG =
  "https://images.unsplash.com/photo-1517747614396-d21a78b850e8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzd8MHwxfHNlYXJjaHwyfHxwcm9mZXNzaW9uYWwlMjBmb290YmFsbCUyMHBsYXllciUyMHNvY2NlciUyMHN0YWRpdW0lMjBkYXJrfGVufDB8fHx8MTc4NzUyMzczM3ww&ixlib=rb-4.1.0&q=85";

export default function Footer() {
  const { t } = useLang();
  return (
    <footer data-testid="site-footer" className="relative bg-[#0A0A0C] border-t border-[#27272A] overflow-hidden">
      <div className="absolute inset-0 opacity-[0.07]">
        <img src={STADIUM_IMG} alt="" className="w-full h-full object-cover" />
      </div>
      <div className="relative max-w-7xl mx-auto px-6 lg:px-10 py-20 lg:py-28">
        <h2 className="font-display font-bold uppercase tracking-tight leading-none text-[13vw] lg:text-[9rem] text-white/10 select-none">
          Dream<span className="text-[#C5A059]/25">2</span>Reality
        </h2>

        <div className="grid md:grid-cols-3 gap-12 mt-16">
          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-[#C5A059] mb-4">{t("footer.agency")}</p>
            <p className="text-sm text-[#71717A] leading-relaxed max-w-xs">
              {t("footer.text")}
            </p>
            <div className="mt-5 space-y-2">
              <a href="mailto:contact@dream2realityfa.com" data-testid="footer-email-link" className="block text-sm text-[#71717A] hover:text-[#C5A059] transition-colors duration-300">contact@dream2realityfa.com</a>
              <a href="tel:+12267597679" data-testid="footer-phone-canada" className="block text-sm text-[#71717A] hover:text-[#C5A059] transition-colors duration-300">+1 (226) 759-7679 — Canada</a>
              <a href="tel:+243846846760" data-testid="footer-phone-rdc" className="block text-sm text-[#71717A] hover:text-[#C5A059] transition-colors duration-300">+243 846 846 760 — Kinshasa, RDC</a>
              <p data-testid="footer-adresse" className="text-sm text-[#71717A] leading-relaxed pt-2 border-t border-[#27272A] mt-3">
                Hôtel Vey Plaza — Avenue Maman Vey n°1, Rue Météo, Lukunga, Commune de Ngaliema, Kinshasa
              </p>
            </div>
          </div>
          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-[#C5A059] mb-4">{t("footer.nav")}</p>
            <div className="flex flex-col gap-3">
              {[["#apropos", t("nav.about")], ["#services", t("nav.services")], ["#talents", t("nav.talents")], ["/detection", t("nav.cta")], ["/portail", "Portail Joueur"], ["#contact", t("nav.contact")], ["/admin", t("footer.admin")]].map(([href, label]) => (
                <a key={href} href={href} data-testid={`footer-link-${label.toLowerCase()}`} className="text-sm text-[#71717A] hover:text-[#C5A059] transition-colors duration-300">
                  {label}
                </a>
              ))}
            </div>
          </div>
          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-[#C5A059] mb-4">{t("footer.follow")}</p>
            <div className="flex flex-wrap gap-4">
              {SOCIALS.map(({ icon: Icon, label, href }) => (
                <a
                  key={label}
                  href={href}
                  target="_blank"
                  rel="noreferrer"
                  aria-label={label}
                  data-testid={`footer-social-${label}`}
                  className="w-11 h-11 border border-[#27272A] flex items-center justify-center text-[#71717A] hover:border-[#C5A059] hover:text-[#C5A059] transition-colors duration-300"
                >
                  <Icon size={18} />
                </a>
              ))}
            </div>
          </div>
        </div>

        <div className="mt-20 pt-8 border-t border-[#27272A] flex flex-wrap justify-between gap-4">
          <p className="text-xs text-[#71717A]">{t("footer.rights")}</p>
          <p className="text-xs text-[#71717A]">Kinshasa, RDC — Canada</p>
        </div>
      </div>
    </footer>
  );
}
